import { Row, Col, Button, Icon, Table as AntdTable, Input } from 'antd';
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Dispatch } from 'redux';
import TableColumn from '../../../../models/Table/TableColumn';
import { fetchMyCustomers } from '../../../../core-module/redux/Customer/actions';
import { TitledContainer } from '../../../../components/TitledContainer/TitledContainer';
import { hasRole, RoleTypes } from '../../../../core-module/helpers/RoleHelper';
import { AppState } from '../../../../core-module/redux/reducers';
import Concat from '../../../../models/Table/Render/Concat';

interface OwnProps extends RouteComponentProps {
  dispatch: Dispatch;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

class Customers extends React.Component<Props> {
  private readonly columns: TableColumn[];

  routeFilter: any;
  private timeout;

  constructor(props: Props) {
    super(props);

    this.timeout = null;

    this.columns = [
      new TableColumn('Voornaam', 'first_name', 'first_name').setAdditional({
        sorter: (a, b) => (a.first_name.toLowerCase() > b.first_name.toLowerCase() ? 1 : -1)
      }),
      new TableColumn('Achternaam', '', '')
        .setAdditional({
          sorter: (a, b) => (a.last_name.toLowerCase() > b.last_name.toLowerCase() ? 1 : -1),
          defaultSortOrder: 'ascend'
        })
        .setRender((rowData) => {
          return rowData.prefix ? rowData.prefix + ' ' + rowData.last_name : rowData.last_name;
        }),
      new TableColumn('Postcode', 'postal_code', 'postal_code').setAdditional({
        sorter: (a, b) => a.postal_code.toLowerCase() > b.postal_code.toLowerCase()
      }),
      new TableColumn('Adres', '', 'address')
        .setRender(new Concat({ keys: ['street_name', 'door_number'] }).render)
        .setAdditional({
          sorter: (a, b) => (a.street_name.toLowerCase() > b.street_name.toLowerCase() ? 1 : -1)
        }),
      new TableColumn('Klantnummer', 'customer_id', 'customer_id').setAdditional({
        sorter: (a, b) => (a.customer_id > b.customer_id ? 1 : -1)
      }),
      new TableColumn('Acties', '', 'actions')
        .setAdditional({ width: '100px' })
        .setRender((rowData) => {
          return (
            <Button
              style={{ marginRight: 5, top: '-1px' }}
              onClick={() => this.openCustomerPage(rowData)}
              type='primary'
              htmlType='button'
            >
              <Icon type='eye' />
            </Button>
          );
        })
    ];

    const driver = this.props.auth.user.driver;
    const start: string = moment().subtract(1, 'month').format('YYYY-MM-DD');
    const end: string = moment().add(2, 'days').format('YYYY-MM-DD');
    let isDepotAdmin =
      hasRole(this.props.auth.roles, RoleTypes.DEPOT_ADMIN) ||
      hasRole(this.props.auth.roles, RoleTypes.DISTRIBUTOR_ADMIN);

    this.routeFilter = {
      filter: {
        include: [
          {
            relation: 'orders',
            scope: {
              fields: ['id', 'customer_id', 'postal_code', 'door_number', 'street_name', 'city'],
              include: [
                {
                  relation: 'customer',
                  scope: {
                    fields: [
                      'first_name',
                      'last_name',
                      'prefix',
                      'customer_id',
                      'camasys_id',
                      'postal_code',
                      'door_number',
                      'street_name',
                      'city'
                    ]
                  }
                }
              ]
            }
          }
        ],
        where: {
          and: [{ actual_date_driven: { between: [start, end] } }]
        },
        fields: ['id', 'date_driven', 'actual_date_driven', 'driver_id'],
        order: 'actual_date_driven DESC'
      }
    };

    isDepotAdmin
      ? this.routeFilter.filter.where.and.push({ depot_id: driver!.depot_id })
      : this.routeFilter.filter.where.and.push({ driver_id: driver!.id });
  }

  componentDidMount() {
    document.title = 'Logistics | Klanten';
    this.props.dispatch(fetchMyCustomers(this.routeFilter));
  }

  openCustomerPage = (rowData) => {
    this.props.history.push(`${this.props.match.url}/${rowData.id}`, { customer: rowData });
  };

  onChange = (value) => {
    clearTimeout(this.timeout);
    const searchValue = value.target.getAttribute('value');
    const search = this.onSearch;

    this.timeout = setTimeout(function () {
      search(searchValue);
    }, 400);
  };

  onSearch = (value: string) => {
    let searchFields = [
      'first_name',
      'last_name',
      'customer_id',
      'postal_code',
      'door_number',
      'street_name',
      'city'
    ];
    let searchFilters: any = null;

    if (value) {
      const clauses: Array<any> = [];
      const fields = searchFields.filter((item) => item.includes('.') === false);
      for (let i = 0; i < fields.length; i++) {
        clauses.push({ [fields[i]]: { like: `%25${value}%25` } });
      }

      searchFilters = {
        filter: {
          where: {
            or: clauses
          }
        }
      };
    }

    let theFilters: any = JSON.parse(JSON.stringify(this.routeFilter));
    if (searchFilters) {
      theFilters.filter.where = { ...theFilters.filter.where, ...searchFilters.filter.where };
    }

    this.props.dispatch(fetchMyCustomers(theFilters));
  };

  render() {
    const columns = this.columns.map((column: TableColumn) => column.get());

    return (
      <Row gutter={12}>
        <Col xs={24}>
          <TitledContainer title={'Klanten'}>
            <>
              <Row style={{ marginBottom: 10 }}>
                <Col xs={24} sm={18}>
                  <em style={{ color: '#aaa' }}>Klanten van afgelopen 4 weken op jouw route</em>
                </Col>
                <Col xs={24} sm={6}>
                  <Input
                    placeholder='Zoek in tabel'
                    onKeyUp={this.onChange}
                    suffix={<Icon type='search' />}
                  />
                </Col>
              </Row>
              <AntdTable
                rowKey={(row, index) => index.toString()}
                rowClassName={() => 'striped-table-row'}
                bordered={true}
                size='small'
                loading={this.props.customer.myCustomersLoading}
                columns={columns}
                dataSource={this.props.customer.myCustomers}
                pagination={false}
                locale={{ emptyText: 'Geen data beschikbaar' }}
              />
            </>
          </TitledContainer>
        </Col>
      </Row>
    );
  }
}

const mapStateToProps = ({ customer, auth }: AppState) => ({
  customer,
  auth
});

export default connect(mapStateToProps)(withRouter(Customers));
